/**
 * AstrologerAvailability Page
 * Allows astrologers to set their availability for consultations
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import DatePicker from '../components/common/DatePicker';
import SlotSelector from '../components/availability/SlotSelector';
import {
  setSelectedDate,
  saveAvailability,
  fetchAvailableDates,
  resetSaveStatus
} from '../store/slices/availabilitySlice';
import { formatDateForAPI, formatDateForDisplay } from '../utils/dateUtils';

const AstrologerAvailability = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const astrologerId = user?.astrologer_id || user?.id;
  const {
    selectedDate,
    selectedSlots,
    saveStatus,
    error,
    availableDates
  } = useSelector(state => state.availability);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    // Fetch available dates on mount
    if (astrologerId) {
      const today = formatDateForAPI(new Date());
      const maxDate = formatDateForAPI(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
      dispatch(fetchAvailableDates({ astrologerId, startDate: today, endDate: maxDate }));
    }
  }, [dispatch, astrologerId]);

  useEffect(() => {
    // Handle save status
    if (saveStatus === 'success') {
      setSnackbar({
        open: true,
        message: `Availability saved successfully for ${formatDateForDisplay(selectedDate)}!`,
        severity: 'success'
      });
      dispatch(resetSaveStatus());
      // Refresh available dates
      const today = formatDateForAPI(new Date());
      const maxDate = formatDateForAPI(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
      dispatch(fetchAvailableDates({ astrologerId, startDate: today, endDate: maxDate }));
    } else if (saveStatus === 'error') {
      setSnackbar({
        open: true,
        message: `Error: ${error?.message || error || 'Failed to save availability'}`,
        severity: 'error'
      });
      dispatch(resetSaveStatus());
    }
  }, [saveStatus, error, dispatch, astrologerId, selectedDate]);

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = formatDateForAPI(date);
      dispatch(setSelectedDate(formattedDate));
      console.log('[AstrologerAvailability] Date changed to:', formattedDate);
    }
  };

  const handleSave = () => {
    if (!selectedDate || selectedSlots.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select a date and at least one time slot',
        severity: 'warning'
      });
      return;
    }

    console.log('[AstrologerAvailability] Saving availability:', { selectedDate, selectedSlots });
    dispatch(saveAvailability({
      date: selectedDate,
      slots: selectedSlots
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isSaveDisabled = () => {
    return !selectedDate || selectedSlots.length === 0 || saveStatus === 'loading';
  };

  const getHighlightedDates = () => {
    return availableDates.map(d => d.date);
  };

  const getTotalSlotsSelected = () => {
    return selectedSlots.reduce((sum, group) => sum + group.slot_indices.length, 0);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Manage Your Availability
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          Select a date and time slots when you're available for consultations. Each slot is 15 minutes.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <DatePicker
            value={selectedDate ? new Date(selectedDate) : null}
            onChange={handleDateChange}
            label="Select Date"
            highlightedDates={getHighlightedDates()}
          />
        </Box>

        {selectedDate && (
          <>
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Setting availability for: {formatDateForDisplay(selectedDate)}
            </Typography>

            <SlotSelector />

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {getTotalSlotsSelected()} slot{getTotalSlotsSelected() !== 1 ? 's' : ''} selected
                ({getTotalSlotsSelected() * 15} minutes total)
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleSave}
                disabled={isSaveDisabled()}
                startIcon={saveStatus === 'loading' ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {saveStatus === 'loading' ? 'Saving...' : 'Save Availability'}
              </Button>
            </Box>
          </>
        )}

        {!selectedDate && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              Select a date above to start setting your availability
            </Typography>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AstrologerAvailability;
